import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Cable, CheckCircle2, Download, Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY_PROFILE = {
  name: "", adapter: "rest_json", version: "", endpoint: "", remote_path: "",
  sender_id: "", receiver_id: "", buyer_id: "", supplier_id: "",
  id_scheme: "", secret_ref: "", is_active: false, config: {},
};

export default function BuyingGroup() {
  const { t } = useI18n();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [profileEdit, setProfileEdit] = useState(null);
  const caps = useQuery({
    queryKey: ["buying-group-capabilities"],
    queryFn: async () => (await api.get("/buying-group/capabilities")).data,
  });
  const outgoing = useQuery({
    queryKey: ["buying-group-outgoing"],
    queryFn: async () => (await api.get("/buying-group/outgoing")).data,
  });
  const profiles = useQuery({
    queryKey: ["buying-group-profiles"],
    queryFn: async () => (await api.get("/buying-group/profiles")).data,
    enabled: can("settings:read"),
  });
  const adapterMap = useMemo(() => Object.fromEntries(
    (caps.data?.standard_adapters || []).map((item) => [item.type, item]),
  ), [caps.data]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/buying-group/outgoing/${id}/status`, { status });
      qc.invalidateQueries({ queryKey: ["buying-group-outgoing"] });
      toast.success(t("buying_group.status_saved"));
    } catch (error) { toast.error(apiErrorMessage(error, t)); }
  };
  const download = (id, format, profileId = "") => {
    const query = new URLSearchParams({ format });
    if (profileId) query.set("profile_id", profileId);
    window.open(`/api/buying-group/outgoing/${id}/export?${query}`, "_blank", "noopener,noreferrer");
  };
  const saveProfile = async () => {
    try {
      if (profileEdit.id) await api.put(`/buying-group/profiles/${profileEdit.id}`, profileEdit);
      else await api.post("/buying-group/profiles", profileEdit);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["buying-group-profiles"] }),
        qc.invalidateQueries({ queryKey: ["buying-group-capabilities"] }),
      ]);
      setProfileEdit(null);
      toast.success(t("buying_group.profile_saved"));
    } catch (error) { toast.error(apiErrorMessage(error, t)); }
  };
  const validateProfile = async (id) => {
    try {
      const response = await api.post(`/buying-group/profiles/${id}/validate`);
      qc.invalidateQueries({ queryKey: ["buying-group-profiles"] });
      qc.invalidateQueries({ queryKey: ["buying-group-capabilities"] });
      toast.success(response.data.status === "configuration_ready"
        ? t("buying_group.configuration_ready") : t("buying_group.profile_incomplete"));
    } catch (error) { toast.error(apiErrorMessage(error, t)); }
  };
  const deleteProfile = async (profile) => {
    if (!window.confirm(t("buying_group.delete_confirm"))) return;
    try {
      await api.delete(`/buying-group/profiles/${profile.id}`);
      qc.invalidateQueries({ queryKey: ["buying-group-profiles"] });
      qc.invalidateQueries({ queryKey: ["buying-group-capabilities"] });
      toast.success(t("buying_group.profile_deleted"));
    } catch (error) { toast.error(apiErrorMessage(error, t)); }
  };
  const updateEdit = (key, value) => setProfileEdit((current) => ({ ...current, [key]: value }));

  return (
    <div className="space-y-6" data-testid="buying-group-page">
      <PageHeader title={t("buying_group.title")} subtitle={t("buying_group.subtitle")} />
      <Card className="p-4">
        <div className="flex items-start gap-3">
          {caps.data?.production_ready
            ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
            : <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />}
          <div className="min-w-0">
            <h3 className="font-semibold">{t("buying_group.readiness")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {caps.data?.production_ready ? t("buying_group.ready") : t("buying_group.partner_required")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge className="bg-emerald-700">{t("buying_group.file_ready")}</Badge>
              {(caps.data?.standard_adapters || []).map((item) => (
                <Badge key={item.type} variant="outline">{item.label}</Badge>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {can("settings:read") && <Card data-testid="buying-group-profiles">
        <div className="flex items-center justify-between gap-3 border-b p-4">
          <div>
            <h3 className="font-semibold">{t("buying_group.partner_profiles")}</h3>
            <p className="text-sm text-muted-foreground">{t("buying_group.partner_profiles_hint")}</p>
          </div>
          {can("settings:write") && <Button onClick={() => setProfileEdit({ ...EMPTY_PROFILE })} data-testid="buying-profile-new">
            <Plus className="mr-1 h-4 w-4" />{t("buying_group.add_profile")}
          </Button>}
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t("buying_group.partner")}</TableHead>
            <TableHead>{t("buying_group.adapter")}</TableHead>
            <TableHead>{t("common.status")}</TableHead>
            <TableHead>{t("common.active")}</TableHead>
            <TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(profiles.data || []).map((profile) => <TableRow key={profile.id}>
              <TableCell className="font-medium">{profile.name}</TableCell>
              <TableCell>{adapterMap[profile.adapter]?.label || profile.adapter}</TableCell>
              <TableCell>
                <Badge variant={profile.status === "configuration_ready" ? "default" : "outline"}>
                  {profile.status}
                </Badge>
                {!!profile.missing_fields?.length && <div className="mt-1 text-xs text-muted-foreground">
                  {t("buying_group.missing")}: {profile.missing_fields.join(", ")}
                </div>}
              </TableCell>
              <TableCell>{profile.is_active ? t("common.yes") : t("common.no")}</TableCell>
              <TableCell className="space-x-1 text-right">
                {can("settings:write") && <>
                  <Button size="sm" variant="outline" onClick={() => validateProfile(profile.id)}>
                    <ShieldCheck className="mr-1 h-4 w-4" />{t("buying_group.check")}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setProfileEdit({ ...profile })} aria-label={t("common.edit")}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteProfile(profile)} aria-label={t("common.delete")}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </>}
              </TableCell>
            </TableRow>)}
            {(profiles.data || []).length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
              {t("buying_group.no_profiles")}
            </TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>}

      <Card>
        <div className="border-b p-4"><h3 className="font-semibold">{t("buying_group.outgoing")}</h3>
          <p className="text-sm text-muted-foreground">{t("buying_group.outgoing_hint")}</p></div>
        <Table>
          <TableHeader><TableRow><TableHead>{t("orders.number")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead>{t("buying_group.created")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {(outgoing.data || []).map((event) => <TableRow key={event.id} data-testid={`buying-event-${event.id}`}>
              <TableCell className="font-medium">{event.payload?.order_number || event.payload?.order || "—"}</TableCell>
              <TableCell><Badge variant="outline">{event.status}</Badge></TableCell>
              <TableCell>{event.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
              <TableCell className="space-x-1 text-right">
                <Button size="sm" variant="outline" onClick={() => download(event.id, "csv")}><Download className="mr-1 h-4 w-4" />CSV</Button>
                <Button size="sm" variant="outline" onClick={() => download(event.id, "json")}><Download className="mr-1 h-4 w-4" />JSON</Button>
                {(profiles.data || []).filter((profile) => profile.is_active && profile.status === "configuration_ready" && adapterMap[profile.adapter]?.export_format)
                  .map((profile) => <Button key={profile.id} size="sm" variant="outline"
                    onClick={() => download(event.id, adapterMap[profile.adapter].export_format, profile.id)}>
                    <Download className="mr-1 h-4 w-4" />{profile.name}
                  </Button>)}
                {can("purchasing:write") && event.status !== "acknowledged" && <Button size="sm" onClick={() => setStatus(event.id, "acknowledged")}><CheckCircle2 className="mr-1 h-4 w-4" />{t("buying_group.acknowledge")}</Button>}
              </TableCell>
            </TableRow>)}
            {(outgoing.data || []).length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground"><Cable className="mx-auto mb-2 h-8 w-8" />{t("buying_group.no_outgoing")}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!profileEdit} onOpenChange={(open) => !open && setProfileEdit(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="buying-profile-dialog">
          <DialogHeader>
            <DialogTitle>{profileEdit?.id ? t("buying_group.edit_profile") : t("buying_group.add_profile")}</DialogTitle>
            <DialogDescription>{t("buying_group.profile_security_hint")}</DialogDescription>
          </DialogHeader>
          {profileEdit && <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("buying_group.partner")}><Input value={profileEdit.name || ""} onChange={(e) => updateEdit("name", e.target.value)} data-testid="buying-profile-name" /></Field>
            <Field label={t("buying_group.adapter")}><select className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              value={profileEdit.adapter} onChange={(e) => updateEdit("adapter", e.target.value)} data-testid="buying-profile-adapter">
              {(caps.data?.standard_adapters || []).map((item) => <option key={item.type} value={item.type}>{item.label}</option>)}
            </select></Field>
            <Field label={t("buying_group.version")}><Input value={profileEdit.version || ""} onChange={(e) => updateEdit("version", e.target.value)} /></Field>
            <Field label={t("buying_group.endpoint")}><Input value={profileEdit.endpoint || ""} onChange={(e) => updateEdit("endpoint", e.target.value)} placeholder="https://… / sftp://…" /></Field>
            <Field label={t("buying_group.remote_path")}><Input value={profileEdit.remote_path || ""} onChange={(e) => updateEdit("remote_path", e.target.value)} /></Field>
            <Field label={t("buying_group.vault_reference")}><Input value={profileEdit.secret_ref || ""} onChange={(e) => updateEdit("secret_ref", e.target.value)} placeholder="mt-erp/partner/…" /></Field>
            <Field label={t("buying_group.sender_id")}><Input value={profileEdit.sender_id || ""} onChange={(e) => updateEdit("sender_id", e.target.value)} /></Field>
            <Field label={t("buying_group.receiver_id")}><Input value={profileEdit.receiver_id || ""} onChange={(e) => updateEdit("receiver_id", e.target.value)} /></Field>
            <Field label={t("buying_group.buyer_id")}><Input value={profileEdit.buyer_id || ""} onChange={(e) => updateEdit("buyer_id", e.target.value)} /></Field>
            <Field label={t("buying_group.supplier_id")}><Input value={profileEdit.supplier_id || ""} onChange={(e) => updateEdit("supplier_id", e.target.value)} /></Field>
            <Field label={t("buying_group.id_scheme")}><Input value={profileEdit.id_scheme || ""} onChange={(e) => updateEdit("id_scheme", e.target.value)} placeholder="0088 (GLN)" /></Field>
            <div className="flex items-center gap-3 sm:col-span-2">
              <Switch checked={!!profileEdit.is_active} onCheckedChange={(value) => updateEdit("is_active", value)} id="partner-active" />
              <Label htmlFor="partner-active">{t("buying_group.activate_profile")}</Label>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileEdit(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveProfile} disabled={!profileEdit?.name || !profileEdit?.adapter} data-testid="buying-profile-save">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <Label className="block space-y-1.5"><span>{label}</span>{children}</Label>;
}
